import { defineStore } from 'pinia'
import { ref } from 'vue'
import planUsageService, {
  isFeatureEnabled as isFeatureEnabledInSnapshot,
  type TenantPlanUsage,
} from '@/services/planUsage.service'

/**
 * `unknown` is deliberately distinct from `disabled`: it means no snapshot has
 * loaded (or the load failed), so the UI must not lock anyone out on a transient
 * error. Only a *successfully loaded* snapshot can report `disabled`.
 */
export type FeatureState = 'unknown' | 'enabled' | 'disabled'

export type PlanUsageStatus = 'idle' | 'loading' | 'loaded' | 'error'

/**
 * Tenant plan-usage snapshot, cached once per session and shared by every
 * entitlement consumer (sidebar lock, header poll, page upsell panel).
 *
 * Single-flight: concurrent `ensureLoaded()` calls share one request, and the
 * promise is cleared afterwards so a `force` call can replace a stale snapshot.
 */
export const usePlanUsageStore = defineStore('planUsage', () => {
  const snapshot = ref<TenantPlanUsage | null>(null)
  const status = ref<PlanUsageStatus>('idle')

  let loadPromise: Promise<void> | null = null

  function featureState(feature: string): FeatureState {
    if (status.value !== 'loaded') return 'unknown'
    return isFeatureEnabledInSnapshot(snapshot.value, feature) ? 'enabled' : 'disabled'
  }

  function isFeatureEnabled(feature: string): boolean {
    return featureState(feature) === 'enabled'
  }

  function isFeatureDisabled(feature: string): boolean {
    return featureState(feature) === 'disabled'
  }

  /** Never rejects: a failed load leaves the state `unknown`, which renders unlocked. */
  async function ensureLoaded(force = false): Promise<void> {
    if (!force && status.value === 'loaded') return
    if (loadPromise) return loadPromise

    loadPromise = load()
    try {
      await loadPromise
    } finally {
      loadPromise = null
    }
  }

  async function load(): Promise<void> {
    status.value = 'loading'
    try {
      snapshot.value = await planUsageService.getPlanUsage()
      status.value = 'loaded'
    } catch {
      snapshot.value = null
      status.value = 'error'
    }
  }

  return {
    snapshot,
    status,
    featureState,
    isFeatureEnabled,
    isFeatureDisabled,
    ensureLoaded,
  }
})
